import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

const services = [
  {
    tier: 'Audit',
    name: 'Growth Bottleneck Audit',
    price: 'One-time engagement',
    duration: '2-4 weeks',
    description: 'Identify the structural constraints limiting your growth. We analyze positioning, market fit, pricing, narrative, and product structure to find what is blocking progress.',
    deliverables: [
      'Bottleneck identification report',
      'Constraint analysis across business model',
      'Prioritized removal roadmap',
      'Expected impact assessment',
    ],
    ideal: 'Companies ready to identify their biggest growth blocker',
  },
  {
    tier: 'Execution',
    name: 'Bottleneck Removal Strategy',
    price: 'Project-based',
    duration: '4-12 weeks',
    description: 'Remove the structural constraints blocking growth. We redesign key elements of your business to eliminate bottlenecks and unlock natural growth.',
    deliverables: [
      'Structural redesign execution',
      'Bottleneck removal implementation',
      'Before and after measurement',
      'System documentation',
    ],
    ideal: 'Companies ready to remove identified bottlenecks',
  },
  {
    tier: 'Advisory',
    name: 'Founder Advisory',
    price: 'Ongoing retainer',
    duration: 'Monthly',
    description: 'Continuous bottleneck identification and removal guidance. Strategic support for founders who want ongoing help finding and eliminating growth constraints.',
    deliverables: [
      'Monthly strategic sessions',
      'On-demand bottleneck analysis',
      'Constraint removal support',
      'Decision clarity and speed',
    ],
    ideal: 'Founders who want continuous strategic support',
  },
];

interface Service {
  tier: string;
  name: string;
  price: string;
  duration: string;
  description: string;
  deliverables: string[];
  ideal: string;
}

const ServiceCard = ({ service }: { service: Service }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: cardProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'center center'],
  });

  const cardOpacity = useTransform(cardProgress, [0, 0.3, 0.6], [0, 0.5, 1]);
  const cardY = useTransform(cardProgress, [0, 0.6], [80, 0]);
  const cardScale = useTransform(cardProgress, [0, 0.6], [0.9, 1]);

  return (
    <motion.div
      ref={cardRef}
      style={{ opacity: cardOpacity, y: cardY, scale: cardScale }}
      className="bg-dark-card border border-dark-border rounded-2xl md:rounded-3xl p-8 md:p-10 hover:border-accent-blue transition-all duration-300 group flex flex-col min-h-[520px] md:min-h-[580px]"
    >
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <p className="text-xs md:text-sm text-accent-blue font-semibold mb-2 uppercase tracking-wider">
          {service.tier}
        </p>
        <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 md:mb-4 leading-tight">
          {service.name}
        </h3>
        <div className="flex items-baseline gap-3 mb-2">
          <p className="text-lg md:text-xl text-gray-400">{service.price}</p>
        </div>
        <p className="text-sm text-gray-500">{service.duration}</p>
      </div>

      {/* Description */}
      <p className="text-sm md:text-[15px] text-gray-400 leading-relaxed mb-6 md:mb-8">
        {service.description}
      </p>

      {/* Deliverables */}
      <div className="mb-6 md:mb-8 flex-grow">
        <p className="text-sm text-white font-semibold mb-3 md:mb-4">Deliverables:</p>
        <ul className="space-y-2.5 md:space-y-3">
          {service.deliverables.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-gray-400">
              <svg
                className="w-5 h-5 text-accent-blue flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span className="leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Ideal For */}
      <div className="pt-5 md:pt-6 border-t border-dark-border mt-auto">
        <p className="text-xs text-gray-500 mb-2">Ideal for:</p>
        <p className="text-sm text-gray-400 leading-relaxed">{service.ideal}</p>
      </div>
    </motion.div>
  );
};

const ServiceOfferings = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const glowIntensity = useTransform(scrollYProgress, [0.2, 0.5, 0.8], [0.05, 0.15, 0.05]);

  return (
    <section ref={sectionRef} className="relative bg-dark-bg py-20 md:py-32 lg:py-40 overflow-hidden">
      {/* Continuous Background Grid */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(rgba(20, 184, 166, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(20, 184, 166, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      {/* Dynamic Background Glow */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent-blue rounded-full blur-3xl pointer-events-none"
        style={{ opacity: glowIntensity }}
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="mb-12 md:mb-16 lg:mb-20"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
            Service Offerings
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-gray-400 max-w-3xl">
            Three ways to identify and remove growth bottlenecks
          </p>
        </motion.div>

        {/* Services Grid - Progressive Reveal */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service) => (
            <ServiceCard key={service.name} service={service} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceOfferings;
